import { ObservableMap } from "mobx";
import { BetSlipOffer, EventType, LiveStatus, Event, Team } from "@gp/models";
import { ISpecifiers, NameProvider } from "@gp/utility";
import { createDisplayTip } from "@gp/slip";
import { EventFilterParams } from "@gp/hub";
import { EventKeyOffer, LookupsStore, SportOffer } from "@gp/offer";

import { BetOffer } from "@models";
import { BetSlipEvent } from "@api-types/agency/bet-slip/BetSlipEvent";
import PeriodConverter, { Period } from "@state/common/PeriodConverter";
import { StatisticsType } from "@data-types/sport-stats/SportStatsTypes";

export function getTip(offer: BetOffer | BetSlipEvent) {
	if (offer.sportData.eventType != EventType.NORMAL) {
		if (offer.playerId != null) {
			return `${offer.playerLastName}, ${offer.playerFirstName}`;
		} else if (offer.teamId != null) {
			return offer.teamName;
		}
		return offer.bettingTypeTip;
	}
	const nameProvider = new NameProvider();
	const offerSpecifier = getOfferSpecifier(offer);
	const bettingTypeTip = nameProvider.getName(
		offer.bettingTypeTip,
		offerSpecifier
	);
	return bettingTypeTip;
}

export function getType(offer: BetOffer | BetSlipEvent | BetSlipOffer) {
	const nameProvider = new NameProvider();
	const offerSpecifier = getOfferSpecifier(offer);
	const bettingType = nameProvider.getName(
		offer.sportData.bettingTypeNameForBettingSlip,
		offerSpecifier
	);
	return bettingType;
}

export function preferLookupBettingTypeName(
	lookupStore: LookupsStore,
	bettingTypeId: string | null | undefined,
	persistedName: string
): string {
	const fromLookups = bettingTypeId
		? lookupStore.bettingTypes.get(bettingTypeId)?.nameForBettingSlip
		: null;

	return fromLookups || persistedName;
}

export function getSlipType(
	offer: {
		bettingTypeId: string;
		sportData: { bettingTypeNameForBettingSlip: string };
	},
	lookupStore: LookupsStore,
	specifiers: ISpecifiers
) {
	const template = preferLookupBettingTypeName(
		lookupStore,
		offer.bettingTypeId,
		offer.sportData.bettingTypeNameForBettingSlip
	);

	return new NameProvider().getName(template, specifiers);
}

export function getSlipTip(
	offer: {
		bettingTypeId: string;
		playerId?: string | null;
		playerFirstName?: string | null;
		playerLastName?: string | null;
		teamId?: string | null;
		teamName?: string | null;
		isEventLocked?: boolean;
		isKeyLocked?: boolean;
		isOfferLocked?: boolean;
		isDeleted?: boolean;
	},
	lookupStore: LookupsStore,
	nameProvider: NameProvider
): string | null {
	if (offer.playerId) {
		const persistedName = [offer.playerLastName, offer.playerFirstName]
			.filter(Boolean)
			.join(", ");

		return lookupStore.players.has(offer.playerId) || !persistedName
			? createDisplayTip(offer, lookupStore, nameProvider)
			: persistedName;
	}

	if (offer.teamId) {
		return lookupStore.teams.has(offer.teamId) || offer.teamName == null
			? createDisplayTip(offer, lookupStore, nameProvider)
			: offer.teamName;
	}

	const canStillBeLocalized = !(
		offer.isEventLocked ||
		offer.isKeyLocked ||
		offer.isOfferLocked ||
		offer.isDeleted
	);

	if (
		canStillBeLocalized &&
		!lookupStore.bettingTypes.has(offer.bettingTypeId)
	) {
		return null;
	}

	return createDisplayTip(offer, lookupStore, nameProvider);
}

function getOfferSpecifier(offer: BetOffer | BetSlipEvent | BetSlipOffer) {
	return {
		competitor1: offer.sportData.teamOneName,
		competitor2: offer.sportData.teamTwoName,
		...offer.specifier,
	};
}

/**
 * Takes period value as string and returns filter that is used in creating request for prematch offer menu.
 * @param {string} period
 * @returns {Filter}
 */
export function generatePrematchOfferMenuFilter(period: Period) {
	const filter: EventFilterParams = {};

	if (!PeriodConverter.IsOfferHistoryPeriod(period)) {
		filter.liveStatus = LiveStatus.PREMATCH;
	}

	if (period !== "full" && PeriodConverter.IsValidPeriod(period)) {
		filter.startTime = PeriodConverter.CalculateOfferInterval(period);
	}

	return filter;
}

/**
 * Format tennis game score. It can be g0, g15, g30, g40, a
 * @todo This should be computed on event object
 * @returns string that has only number if it starts with 'g' or 'A' if it is 'a', or "0" for nullish
 */
export function formatTennisGameScore(input?: string | number | null): string {
	input = String(input || 0);
	return input.startsWith("g") ? input.substring(1) : input.toUpperCase();
}

/**
 * @param id sport id
 * @param isLive is sport live
 * @param isOutright is sport outright
 * @returns unique sport key based on params
 */
export function getSportKeyId(
	id: string,
	isLive: boolean,
	isOutright: boolean
) {
	if (isLive) {
		return `${id}-live`;
	}

	if (isOutright) {
		return `${id}-outright`;
	}

	return id;
}
/**
 * @param sport sport for which to create unique key
 * @returns unique sport key based on sport
 */
export function getSportOfferKey(sport: SportOffer) {
	return getSportKeyId(sport.id, sport.isLive, sport.isOutright);
}

export function highlightTeam(data: {
	statisticsType: StatisticsType;
	homeValue: number;
	awayValue: number;
}) {
	let homeTeamHighlight = false;
	let awayTeamHighlight = false;

	if (data.statisticsType == StatisticsType.Positive) {
		if (data.homeValue > data.awayValue) {
			homeTeamHighlight = true;
		} else if (data.homeValue < data.awayValue) {
			awayTeamHighlight = true;
		}
	} else if (data.statisticsType == StatisticsType.Negative) {
		if (data.homeValue > data.awayValue) {
			awayTeamHighlight = true;
		} else if (data.homeValue < data.awayValue) {
			homeTeamHighlight = true;
		}
	}

	return {
		homeTeamHighlight: homeTeamHighlight,
		awayTeamHighlight: awayTeamHighlight,
	};
}

export function getBetSpotlightOfferKey(
	eventKeyMap: Map<string, EventKeyOffer>,
	bettingType: string,
	event: Event,
	teams: ObservableMap<string, Team>
): { offerKey: EventKeyOffer; name: string } | null {
	const nameProvider = new NameProvider();

	let bettingTypeKeys = Array.from(eventKeyMap.values()).filter(
		(ek) => ek.bettingType?.abrv == bettingType
	);

	if (bettingTypeKeys?.length < 1) {
		return null;
	}

	let offerKey = bettingTypeKeys[0];

	const marginalKeyOffer = bettingTypeKeys.find(
		(bettingOffer) => bettingOffer.isFavorite
	);
	if (marginalKeyOffer) {
		offerKey = marginalKeyOffer;
	}
	const homeTeam = teams.get(event.teamOneId || "");
	const awayTeam = teams.get(event.teamTwoId || "");

	const specifiers = {
		...offerKey?.specifier,
		competitor1: homeTeam?.name || "",
		competitor2: awayTeam?.name || "",
	};

	const name = nameProvider.getName(
		offerKey.bettingType.nameForOfferList,
		specifiers
	);

	return {
		name,
		offerKey,
	};
}
